const TOKEN_KEY = "bezierclick_admin_token";

const getDefaultStorage = () => {
  if (typeof window === "undefined") {
    return null;
  }
  return window.localStorage;
};

const getApiBaseUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  if (!backendUrl && typeof window !== "undefined") {
    const localHosts = new Set(["localhost", "127.0.0.1", "::1"]);
    if (localHosts.has(window.location.hostname)) {
      return `http://${window.location.hostname === "localhost" ? "localhost" : "127.0.0.1"}:8001/api`;
    }
  }
  return `${backendUrl.replace(/\/$/, "")}/api`;
};

const request = async (path, options = {}) => {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });
  let data = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }
  return { response, data };
};

const readSession = (storage = getDefaultStorage()) => {
  if (!storage) {
    return null;
  }

  try {
    const rawSession = storage.getItem(TOKEN_KEY);
    return rawSession ? JSON.parse(rawSession) : null;
  } catch {
    storage.removeItem(TOKEN_KEY);
    return null;
  }
};

const login = async (
  username,
  password,
  {
    fetchImpl = fetch,
    storage = getDefaultStorage(),
    apiBaseUrl = getApiBaseUrl(),
  } = {}
) => {
  if (!storage) {
    return false;
  }

  try {
    const response = await fetchImpl(`${apiBaseUrl}/admin/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      storage.removeItem(TOKEN_KEY);
      return { ok: false, reason: "invalid_credentials" };
    }

    const data = await response.json();
    if (!data.token || !data.expires_at) {
      storage.removeItem(TOKEN_KEY);
      return { ok: false, reason: "invalid_response" };
    }

    storage.setItem(
      TOKEN_KEY,
      JSON.stringify({
        token: data.token,
        expiresAt: data.expires_at,
        mustChangePassword: !!data.must_change_password,
        user: data.user || null,
      })
    );
    return { ok: true, mustChangePassword: !!data.must_change_password };
  } catch {
    storage.removeItem(TOKEN_KEY);
    return { ok: false, reason: "network_error" };
  }
};

const logout = (storage = getDefaultStorage()) => {
  if (storage) {
    storage.removeItem(TOKEN_KEY);
  }
};

const getToken = (storage = getDefaultStorage()) => {
  const session = readSession(storage);
  if (!session?.token || !session?.expiresAt) {
    return null;
  }

  if (Date.parse(session.expiresAt) <= Date.now()) {
    logout(storage);
    return null;
  }

  return session.token;
};

const isAuthenticated = (storage = getDefaultStorage()) => {
  return !!getToken(storage);
};

const authHeaders = (storage = getDefaultStorage()) => {
  const token = getToken(storage);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const isPasswordChangeRequired = (storage = getDefaultStorage()) => {
  const session = readSession(storage);
  return !!session?.mustChangePassword;
};

const setupStatus = async () => {
  try {
    const { response, data } = await request("/admin/setup-status");
    return response.ok ? { ok: true, setupRequired: !!data.setup_required } : { ok: false };
  } catch {
    return { ok: false };
  }
};

const setupAdmin = async ({ setupToken, username, password }) => {
  try {
    const { response, data } = await request("/admin/setup", {
      method: "POST",
      body: JSON.stringify({ setup_token: setupToken, username, password }),
    });
    return response.ok ? { ok: true } : { ok: false, detail: data.detail };
  } catch {
    return { ok: false, detail: "API indisponivel." };
  }
};

const listUsers = async () => {
  const { response, data } = await request("/admin/users", { headers: authHeaders() });
  return response.ok ? { ok: true, users: data.users || [] } : { ok: false, detail: data.detail };
};

const createUser = async ({ username, password }) => {
  const { response, data } = await request("/admin/users", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ username, password }),
  });
  return response.ok ? { ok: true } : { ok: false, detail: data.detail };
};

const setUserStatus = async (id, active) => {
  const { response, data } = await request(`/admin/users/${id}/status`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ active }),
  });
  return response.ok ? { ok: true } : { ok: false, detail: data.detail };
};

const resetUserPassword = async (id, password) => {
  const { response, data } = await request(`/admin/users/${id}/password`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ password }),
  });
  return response.ok ? { ok: true } : { ok: false, detail: data.detail };
};

const changePassword = async ({ currentPassword, newPassword }, storage = getDefaultStorage()) => {
  const { response, data } = await request("/admin/change-password", {
    method: "POST",
    headers: authHeaders(storage),
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!response.ok) {
    return { ok: false, detail: data.detail };
  }
  const session = readSession(storage);
  if (session) {
    storage.setItem(TOKEN_KEY, JSON.stringify({ ...session, mustChangePassword: false }));
  }
  return { ok: true };
};

module.exports = {
  TOKEN_KEY,
  login,
  logout,
  isAuthenticated,
  getToken,
  isPasswordChangeRequired,
  setupStatus,
  setupAdmin,
  listUsers,
  createUser,
  setUserStatus,
  resetUserPassword,
  changePassword,
};
